import type { APP_CONSTANTS } from '~/common/explicit-exports';
import {
  Body,
  Button,
  Col,
  Link,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  useToast,
  type ModalProps,
} from '@workramp/ui';
import { useState } from 'react';
import AssetPickerTypes from '~/components/common/asset-picker/asset-picker-types';
import { getCurrentUser, I18n } from '~/common/explicit-exports';
import { getContentProviderAdminSettings } from '~/elc/features/integrations/routes/ContentProviderIntegration/utils';
import { useCreateContentProviderContent } from '../../api';
import { ContentProviderContentSearchField } from './ContentProviderContentSearchField';

export interface CreateContentProviderContentModalProps extends Omit<ModalProps, 'children' | 'isOpen'> {
  onCreate: ({
    provider,
    contentId,
    enterpriseId,
  }: {
    provider: (typeof APP_CONSTANTS.TOP_LEVEL_CONTENT_CONTENT_SOURCES)['UDEMY' | 'LINKEDIN_LEARNING'];
    contentId: string;
    enterpriseId: number;
  }) => void;
  isOpen: boolean;
  provider: (typeof APP_CONSTANTS.TOP_LEVEL_CONTENT_CONTENT_SOURCES)['UDEMY' | 'LINKEDIN_LEARNING'];
  itemFolderId?: string;
}

export function CreateContentProviderContentModal({
  provider,
  onCreate,
  onClose,
  itemFolderId,
  ...modalProps
}: CreateContentProviderContentModalProps) {
  const [contentId, setContentId] = useState<string | null>(null);
  const { open: openToast } = useToast();
  const createContentMutation = useCreateContentProviderContent();
  const { documentationLink } = getContentProviderAdminSettings(provider);

  const contentName = AssetPickerTypes.getDisplayName(provider, false);
  const contentNamePlural = AssetPickerTypes.getDisplayName(provider, true);

  const TRANSLATED_TITLE = I18n.t('js.admin.content_creation.content_provider_content.modal_title', {
    provider: contentName,
  });
  const TRANSLATED_DESCRIPTION = I18n.t('js.admin.content_creation.content_provider_content.modal_description');
  const TRANSLATED_LABEL = I18n.t('js.admin.content_creation.content_provider_content.modal_search_label', {
    provider: contentNamePlural,
  });
  const TRANSLATED_PLACEHOLDER = I18n.t('js.admin.content_creation.content_provider_content.modal_search_placeholder', {
    provider: contentNamePlural,
  });
  const TRANSLATED_CREATE = I18n.t('js.admin.content_creation.content_provider_content.modal_cta');

  const handleCreate = () => {
    createContentMutation.mutate(
      {
        provider,
        contentId,
        itemFolderId,
      },
      {
        onSuccess: () => {
          openToast({
            children: I18n.t('js.admin.content_creation.content_provider_content.create_started', {
              provider: contentName,
            }),
          });
          onCreate({ provider, contentId, enterpriseId: getCurrentUser().enterpriseId });
          onClose();
        },
      },
    );
  };

  return (
    <Modal onClose={onClose} {...modalProps}>
      <ModalHeader
        title={TRANSLATED_TITLE}
        description={
          <Body level="md">
            {TRANSLATED_DESCRIPTION}{' '}
            <Link href={documentationLink} target="_blank">
              Learn More
            </Link>
          </Body>
        }
      />
      <ModalBody>
        <ContentProviderContentSearchField
          placeholder={TRANSLATED_PLACEHOLDER}
          label={TRANSLATED_LABEL}
          hideLabel
          onContentSelection={setContentId}
          provider={provider}
        />
      </ModalBody>
      <ModalFooter>
        <Row align="center" gapX={2} justify="flex-end">
          <Col xs="auto">
            <Button size="lg" color="secondary" variant="text" onPress={onClose}>
              {I18n.t('js.common.modal.cancel')}
            </Button>
          </Col>
          <Col xs="auto">
            <Button size="lg" color="primary" onPress={handleCreate} isDisabled={contentId === null}>
              {TRANSLATED_CREATE}
            </Button>
          </Col>
        </Row>
      </ModalFooter>
    </Modal>
  );
}
